import { useState } from "react";
import classNames from "classnames";
import FlagIcon from "@/assets/svgs/flag.svg";
import FlagFilledIcon from "@/assets/svgs/flag-f.svg";
import CaretDownIcon from "@/assets/svgs/caret-down.svg";
import CaretUpIcon from "@/assets/svgs/caret-up.svg";
import ThumbsUpIcon from "@/assets/svgs/thumbs-up.svg";
import ThumbsUpFilledIcon from "@/assets/svgs/thumbs-up-f.svg";
import { Chip } from "@/components/shared/data-display/chip/chip";
import { Button } from "@/components/shared/inputs/button/button";
import { Link } from "@/components/shared/inputs/link/link";
import { logError } from "@/infrastructure/observability/error-logging.service";
import {
  getWorkReviews,
  hideWorkReview,
  reportWorkReview,
  setWorkReviewVote,
} from "@/services/work.service";
import type { WorkReview, WorkSummary } from "@/types/work.types";
import { sanitizePlainText } from "@/utility/html.utility";
import WorkRating from "./work-rating";
import styles from "./customer-reviews.module.scss";
import { legacyLoginLink, legacyUrlString } from "@/utility/url";

const COLLAPSED_REVIEW_CHARACTER_LIMIT = 260;
const reviewDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

const formatReviewDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : reviewDateFormatter.format(date);
};

interface ReviewArticleProps {
  review: WorkReview;
  isLoggedIn: boolean;
  reviewUrl: string;
  loginGuidance: ReviewLoginGuidance | null;
  onSelected: (idWorkReview: number) => void;
  onLoginRequired: (idWorkReview: number, action: ReviewLoginAction) => void;
  onHidden: (idWorkReview: number) => void;
}

type ReviewLoginAction = "like" | "report";

interface ReviewLoginGuidance {
  reviewId: number;
  action: ReviewLoginAction;
  loginUrl: string;
}

const ReviewArticle = (iProps: ReviewArticleProps) => {
  const {
    review,
    isLoggedIn,
    reviewUrl,
    loginGuidance,
    onSelected,
    onLoginRequired,
    onHidden,
  } = iProps;
  const [expanded, setExpanded] = useState(false);
  const [likes, setLikes] = useState(review.likes);
  const [voteValue, setVoteValue] = useState(review.userVoteValue);
  const [reported, setReported] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    "vote" | "report" | "hide" | null
  >(null);
  const [actionError, setActionError] = useState("");
  const body = sanitizePlainText(review.body);
  const headline =
    sanitizePlainText(review.headline) ||
    `${body.slice(0, 50)}${body.length > 50 ? "…" : ""}`;
  const customerName =
    sanitizePlainText(review.customerName) || "ThriftBooks.com User";
  const isLongReview = body.length > COLLAPSED_REVIEW_CHARACTER_LIMIT;
  const isLiked = voteValue === 1;

  const handleVote = async () => {
    if (pendingAction) return;

    if (!isLoggedIn) {
      setActionError("");
      onLoginRequired(review.idWorkReview, "like");
      return;
    }

    const previousVote = voteValue;
    const previousLikes = likes;
    const nextVote = isLiked ? 0 : 1;
    setVoteValue(nextVote);
    setLikes(Math.max(0, previousLikes + (nextVote === 1 ? 1 : -1)));
    setPendingAction("vote");
    setActionError("");

    try {
      setVoteValue(await setWorkReviewVote(review.idWorkReview, nextVote));
    } catch (error) {
      setVoteValue(previousVote);
      setLikes(previousLikes);
      setActionError("We couldn't save your vote. Please try again.");
      logError(error, { context: "CustomerReviews:setReviewVote" });
    } finally {
      setPendingAction(null);
    }
  };

  const handleReport = async () => {
    if (pendingAction || reported) return;

    if (!isLoggedIn) {
      setActionError("");
      onLoginRequired(review.idWorkReview, "report");
      return;
    }

    setPendingAction("report");
    setActionError("");
    try {
      setReported(await reportWorkReview(review.idWorkReview));
    } catch (error) {
      setActionError("We couldn't report this review. Please try again.");
      logError(error, { context: "CustomerReviews:reportReview" });
    } finally {
      setPendingAction(null);
    }
  };

  const handleHide = async () => {
    if (pendingAction) return;

    setPendingAction("hide");
    setActionError("");
    try {
      if (await hideWorkReview(review.idWorkReview)) {
        onHidden(review.idWorkReview);
      } else {
        setActionError("We couldn't hide your review. Please try again.");
      }
    } catch (error) {
      setActionError("We couldn't hide your review. Please try again.");
      logError(error, { context: "CustomerReviews:hideReview" });
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <article
      className={classNames(styles.review, {
        [styles.currentUserReview]: review.isCurrentUser,
      })}
      onClickCapture={() => onSelected(review.idWorkReview)}
    >
      <div className={styles.reviewHeading}>
        <span
          className={styles.reviewStars}
          role="img"
          aria-label={`${review.rating} out of 5 stars`}
        >
          <span aria-hidden>
            {"★".repeat(review.rating)}
            {"☆".repeat(5 - review.rating)}
          </span>
        </span>
        <h3>{headline}</h3>
      </div>
      <div className={styles.byline}>
        <span>
          By {customerName}, {formatReviewDate(review.postedAt)}
        </span>
        {review.verifiedPurchase && (
          <Chip label="Verified Purchase" size="small" />
        )}
      </div>
      {!review.isVisible && review.isCurrentUser && (
        <div className={styles.pendingNotice} role="status">
          <strong>Thank you for your review!</strong> Your review is being
          processed and will be publicly viewable soon.
        </div>
      )}
      <p
        className={classNames(styles.body, {
          [styles.collapsed]: isLongReview && !expanded,
        })}
      >
        {body}
      </p>
      {isLongReview && (
        <Button
          text={expanded ? "Show Less" : "Read Full Review"}
          variant="link"
          size="small"
          onClick={() => setExpanded((current) => !current)}
          ariaLabel={expanded ? "Collapse review" : "Read full review"}
          className={styles.readMore}
        />
      )}
      {!review.isCurrentUser && (
        <div className={styles.actions}>
          <span>{likes}</span>
          <Button
            text={isLiked ? "Liked" : "Like"}
            variant="link"
            size="small"
            icon={isLiked ? ThumbsUpFilledIcon : ThumbsUpIcon}
            loading={pendingAction === "vote"}
            onClick={() => void handleVote()}
            ariaLabel={isLiked ? "Unlike this review" : "Like this review"}
          />
          <Button
            text={reported ? "Reported" : "Report"}
            variant="link"
            size="small"
            icon={reported ? FlagFilledIcon : FlagIcon}
            disabled={reported}
            loading={pendingAction === "report"}
            onClick={() => void handleReport()}
            ariaLabel={reported ? "Review reported" : "Report this review"}
          />
        </div>
      )}
      {isLoggedIn && review.isCurrentUser && (
        <div className={styles.ownerActions}>
          <Link
            href={reviewUrl}
            linkType="external"
            rel="nofollow"
            className="u-interactive-text-green"
          >
            Edit
          </Link>
          <span aria-hidden>|</span>
          <Button
            text="Hide"
            variant="link"
            size="small"
            loading={pendingAction === "hide"}
            disabled={pendingAction !== null}
            onClick={() => void handleHide()}
            ariaLabel="Hide your review"
          />
        </div>
      )}
      {loginGuidance && (
        <p role="status" className={styles.actionGuidance}>
          <Link
            href={loginGuidance.loginUrl}
            linkType="external"
            rel="nofollow"
            className="u-interactive-text-green"
            underline="always"
          >
            Log in
          </Link>{" "}
          to {loginGuidance.action === "like" ? "like" : "report"} this review.
        </p>
      )}
      {actionError && (
        <p role="alert" className={styles.actionError}>
          {actionError}
        </p>
      )}
    </article>
  );
};

export interface CustomerReviewsProps {
  work: WorkSummary;
  reviews: WorkReview[];
  isLoggedIn: boolean;
}

/**
 * Displays aggregate work ratings and customer review cards.
 * @param iProps Customer reviews properties.
 * @returns The customer reviews section.
 */
const CustomerReviews = (iProps: CustomerReviewsProps) => {
  const { work, reviews, isLoggedIn } = iProps;
  const reviewUrl = legacyUrlString(
    `/w/${work.cleanUrl}/${work.idWork}/review/`,
  );
  const [additionalReviews, setAdditionalReviews] = useState<
    WorkReview[] | null
  >(null);
  const [showAdditionalReviews, setShowAdditionalReviews] = useState(false);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [reviewLoadError, setReviewLoadError] = useState("");
  const [loginGuidance, setLoginGuidance] =
    useState<ReviewLoginGuidance | null>(null);
  const [hiddenReviewIds, setHiddenReviewIds] = useState<Set<number>>(
    () => new Set(),
  );
  const canShowMore = work.reviewCount > reviews.length;
  const showReviewToggle = canShowMore || additionalReviews !== null;
  const availableReviews =
    showAdditionalReviews && additionalReviews
      ? [...reviews, ...additionalReviews]
      : reviews;
  const displayedReviews = availableReviews.filter(
    (review) => !hiddenReviewIds.has(review.idWorkReview),
  );

  const handleReviewHidden = (idWorkReview: number) => {
    setHiddenReviewIds((current) => new Set(current).add(idWorkReview));
  };

  const handleReviewSelected = (idWorkReview: number) => {
    setLoginGuidance((current) =>
      current?.reviewId === idWorkReview ? current : null,
    );
  };

  const handleLoginRequired = (
    idWorkReview: number,
    action: ReviewLoginAction,
  ) => {
    setLoginGuidance({
      reviewId: idWorkReview,
      action,
      loginUrl: legacyLoginLink(window.location.href),
    });
  };

  const toggleAdditionalReviews = async () => {
    if (showAdditionalReviews) {
      setShowAdditionalReviews(false);
      return;
    }

    if (additionalReviews !== null) {
      setShowAdditionalReviews(true);
      return;
    }

    setIsLoadingReviews(true);
    setReviewLoadError("");
    try {
      // Legacy's procedure returns a different result window when asked for fewer
      // than ten rows. Reload its first ten-row page and remove the six reviews
      // already included in the server-rendered payload.
      const page = await getWorkReviews(work.idWork, 1);
      const initialIds = new Set(reviews.map((review) => review.idWorkReview));
      setAdditionalReviews(
        page.reviews.filter((review) => !initialIds.has(review.idWorkReview)),
      );
      setShowAdditionalReviews(true);
    } catch (error) {
      setReviewLoadError("We couldn't load more reviews. Please try again.");
      logError(error, { context: "CustomerReviews:getMoreReviews" });
    } finally {
      setIsLoadingReviews(false);
    }
  };

  return (
    <section className={styles.root} id="customer-reviews">
      <div className={styles.header}>
        <div>
          <h2 className="u-title-sm">Customer Reviews</h2>
          <div className={styles.ratingSummary}>
            <WorkRating
              value={work.rating}
              count={work.ratingCount}
              showReviewsLink={false}
            />
            <span>
              {work.ratingCount} customer{" "}
              {work.ratingCount === 1 ? "rating" : "ratings"}
              {" | "}
              {work.reviewCount} {work.reviewCount === 1 ? "review" : "reviews"}
            </span>
          </div>
        </div>
        <Button
          text="Write a Review"
          href={reviewUrl}
          rel="nofollow"
          emphasis
          rounded
          className={styles.writeReview}
        />
      </div>
      {displayedReviews.length > 0 ? (
        <div className={styles.reviewGrid}>
          {displayedReviews.map((review) => (
            <ReviewArticle
              key={review.idWorkReview}
              review={review}
              isLoggedIn={isLoggedIn}
              reviewUrl={reviewUrl}
              loginGuidance={
                loginGuidance?.reviewId === review.idWorkReview
                  ? loginGuidance
                  : null
              }
              onSelected={handleReviewSelected}
              onLoginRequired={handleLoginRequired}
              onHidden={handleReviewHidden}
            />
          ))}
        </div>
      ) : (
        <p>
          There are currently no reviews.{" "}
          <Link
            href={reviewUrl}
            linkType="external"
            rel="nofollow"
            className="u-interactive-text-green"
          >
            Be the first to review this work
          </Link>
          .
        </p>
      )}
      {showReviewToggle && (
        <Button
          text={
            showAdditionalReviews
              ? "See fewer customer reviews"
              : "See more customer reviews"
          }
          variant="link"
          icon={showAdditionalReviews ? CaretUpIcon : CaretDownIcon}
          iconPosition="right"
          loading={isLoadingReviews}
          onClick={() => void toggleAdditionalReviews()}
          emphasis
          className={styles.reviewToggle}
        />
      )}
      {reviewLoadError && (
        <p role="alert" className={styles.actionError}>
          {reviewLoadError}
        </p>
      )}
    </section>
  );
};

export default CustomerReviews;
